<template>
    <div :class="componentClasses">
        <handle-component
            v-for="(handle, index) in props.handles"
            :key="index"
            :type="handle.type"
            :position="handle.position"
        />

        <div class="custom-node__container">
            <div class="custom-node__visual">
                <div class="custom-node__icon">
                    <slot name="icon" />
                </div>
            </div>

            <div class="custom-node__description">
                <slot name="content" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Position, Handle as HandleComponent, type HandleType } from '@vue-flow/core';

    type LayoutVariant =
        | 'full'
        | 'simple'
    ;

    interface Handle {
        type: HandleType;
        position: Position;
    }

    interface Props {
        handles?: Handle[],
        variant?: LayoutVariant,
    }

    const props = withDefaults(defineProps<Props>(), {
        handles: () => [
            { type: 'target', position: Position.Top },
            { type: 'source', position: Position.Bottom },
        ],
        variant: 'simple',
    });

    const componentClasses = {
        'custom-node': true,
        [`custom-node--${props.variant}`]: true,
    };
</script>

<style scoped lang="scss">
.custom-node {
    border-radius: size(md);
    box-sizing: border-box;
    width: 512px;
    height: 96px;
    display: flex;
    overflow: hidden;

    &[selected="true"] {
        outline-offset: var(--global-sizes-size--sm, size(sm));
        outline: #ED6E2D dashed 2px;
        background-color: color(red-500);

        :deep(h2), :deep(p), :deep(span) {
            color: white;
        }

        :deep(.node-icon) {
            color: color(red-500);
        }

        .custom-node__visual,
        .custom-node--simple,
        .custom-node--full,
        .custom-node__container {
            background-color: transparent;
            box-shadow: none;

            &:hover {
                background-color: transparent;
            }
        }

        .custom-node__icon {
            background-color: white;
        }
    }

    &:hover {
        cursor: pointer;
    }

    &__container {
        width: 100%;
        display: grid;
        grid-template-columns: min-content auto;
        align-items: center;
        transition: color, background-color;
        transition-duration: .2s;
    }

    &__visual {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: size(md);
    }

    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__description {
        display: flex;
        flex-direction: column;
        padding-right: size(lg);
        padding-left: size(md);
        gap: size(sm);
    }

    &--full {
        @include box-shadow-360();
        transition: color, background-color;
        transition-duration: .2s;

        .custom-node__icon {
            background-color: white;
            border-radius: 100%;
            margin: size(sm);
            padding: size(md);
        }
    }

    &--simple {
        margin-right: -(calc(512px - 96px));

        .custom-node__visual {
            width: 96px;
            height: 96px;
            @include box-shadow-360();
            transition: color, background-color;
            transition-duration: .2s;
            transition-timing-function: ease-in-out;
        }

        .custom-node__icon {
            background-color: white;
            border-radius: 100%;
            padding: size(sm);
            margin: size(md);
        }
    }
}
</style>
